import { AxiosHeaders, AxiosResponse } from 'axios';

import { FamilyGroup, productCatalogueHttp, SegmentType } from '@gn-audio/jss-core';

const emptyResult: EmptyResults = { count: 0, items: [] };

const failedToFetchSearchResultsLabel = 'Failed to fetch search results';

export const getFamilyGroupsWithAttributes = async (familyId: string, locale: string) => {
  const url = `/family/${familyId}/attributes?include=available&marketLocale=${locale}`;
  const response = await productCatalogueHttp.get<FamilyGroup[] | string>(url);
  return response.data;
};

export const getFamilyKSP = async (familyId: string, locale: string) => {
  const url = `/family/${familyId}/keysellingpoints?include=available&marketLocale=${locale}&kspMode=short`;
  const res = await productCatalogueHttp.get<KSP[]>(url);
  if (res.status !== 200) {
    return [];
  }
  return res.data;
};

export const getGroupKSP = async (groupId: string, locale: string) => {
  const url = `/group/${groupId}/keysellingpoints?include=available&marketLocale=${locale}&kspMode=short`;
  const res = await productCatalogueHttp.get<KSP[]>(url);
  if (res.status !== 200) {
    return [];
  }
  return res.data;
};

export const getProductsByANCType = async (
  ancType: '1' | '2' | '3' | '4'
): Promise<SearchResults | EmptyResults> => {
  const headers = new AxiosHeaders();
  headers.set('Content-Type', 'application/json');
  try {
    const url = `/search`;
    const response = await productCatalogueHttp.post<
      unknown,
      AxiosResponse<EmptyResults | SearchResults>
    >(
      url,
      {
        marketLocale: 'en-us',
        search: '',
        filter: `attributes/any(a: a eq 'Product_AncTypePublicWeb|${ancType}')`,
        orderBy: 'discountedPrice desc',
        skip: 0,
        top: 10,
      },
      { headers }
    );

    if (response.status === 204) {
      return { ...emptyResult };
    } else {
      return response.data;
    }
  } catch (error) {
    console.error(`${failedToFetchSearchResultsLabel}:`, error);
    return { ...emptyResult, error: error };
  }
};

export const SegmentTypes: Record<string, SegmentType> = {
  Business: 'Business' as SegmentType,
  Convergence: 'Convergence' as SegmentType,
  Consumer: 'Consumer' as SegmentType,
};

export const getSearchResults = async (
  filter: string,
  locale: string,
  skip = 0,
  numberOfResultsPerPage: number
): Promise<SearchResults | EmptyResults> => {
  const headers = new AxiosHeaders();
  headers.set('Content-Type', 'application/json');
  try {
    const response = await productCatalogueHttp.post<
      unknown,
      AxiosResponse<EmptyResults | SearchResults>
    >(
      `/search`,
      {
        filter,
        orderBy: '',
        skip: skip,
        top: numberOfResultsPerPage,
        marketLocale: locale === 'en' ? 'en-us' : locale,
      },
      { headers }
    );

    if (response.status === 204) {
      return { ...emptyResult };
    } else {
      return response.data;
    }
  } catch (error) {
    console.error(`${failedToFetchSearchResultsLabel}:`, error);
    return { ...emptyResult, error: error };
  }
};

export interface Attribute {
  key: string;
  text?: string;
  value?: string;
  valueInEnUs?: string;
  valueKey: string;
}

export interface SCImage {
  url: string;
}

export interface KSP {
  title: string;
  header: string;
  text: string;
}

export type EmptyResults = {
  count: number;
  items: [];
  error?: unknown;
};

export type SearchResults = {
  count: number;
  items: PIMProduct[];
  error?: unknown;
};

export type PIMProduct = {
  description: string;
  familyId: number;
  familyName: string;
  name: string;
  images: string[];
  pageUrl: string;
  supportPageUrl: string;
  hasBluetoothPairingGuide: boolean;
  segmentType: string;
  summary: string;
  thumbnailUrl: string;
  title: string;
  uniqueSellingPoint: string;
  groupId: string;
};
